import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Bot, Cloud, LayoutGrid, Workflow, BarChart3, Plug, Database, Zap } from "lucide-react";
import { MaskedLine, EASE } from "./Reveal";

const NODES = [
  { label: "AI Agents", Icon: Bot, x: 320, y: 48 },
  { label: "Cloud", Icon: Cloud, x: 528, y: 120 },
  { label: "Apps", Icon: LayoutGrid, x: 576, y: 296 },
  { label: "Workflows", Icon: Workflow, x: 480, y: 452 },
  { label: "Analytics", Icon: BarChart3, x: 320, y: 512 },
  { label: "APIs", Icon: Plug, x: 160, y: 452 },
  { label: "Data", Icon: Database, x: 64, y: 296 },
  { label: "Automation", Icon: Zap, x: 112, y: 120 },
];
const CX = 320, CY = 280;

const PILLARS = ["BUILD", "OPERATE", "AUTOMATE", "GROW"];

export const Hero = ({ onContact }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % PILLARS.length), 1600);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pt-40 pb-24 lg:pt-48 lg:pb-32">
      <div className="pointer-events-none absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full opacity-30 blur-3xl jx-gradient-bg" />
      <div className="pointer-events-none absolute top-1/2 -left-64 w-[480px] h-[480px] rounded-full bg-jx-purple/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-16 items-center relative">
        <motion.div style={{ y: yText }} className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.35 }}
            className="flex items-center gap-3 mb-8"
            data-testid="hero-pillars"
          >
            {PILLARS.map((p, i) => (
              <span key={p} className="flex items-center gap-3">
                <span className={`text-[11px] font-bold tracking-[0.3em] transition-colors duration-500 ${active === i ? "jx-gradient-text" : "text-jx-ink/30"}`}>
                  {p}
                </span>
                {i < PILLARS.length - 1 && <span className="w-1 h-1 rounded-full bg-jx-ink/20" />}
              </span>
            ))}
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] text-jx-ink" data-testid="hero-headline">
            <MaskedLine delay={0.45} immediate>Intelligence</MaskedLine>
            <MaskedLine delay={0.55} immediate>that moves</MaskedLine>
            <MaskedLine delay={0.65} immediate>
              business <span className="jx-gradient-text">forward.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-jx-ink/60"
          >
            JIYONOX combines AI, software engineering, automation and cloud technology to build intelligent digital
            systems that help businesses operate smarter, move faster and grow with confidence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={onContact}
              className="group inline-flex items-center gap-3 rounded-full jx-gradient-bg text-white font-medium px-7 py-4 shadow-[0_16px_40px_rgba(209,45,140,0.3)] transition-[transform,box-shadow] duration-300 hover:scale-[1.04] hover:shadow-[0_20px_50px_rgba(209,45,140,0.4)] active:scale-95"
            >
              Start a Conversation
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => document.querySelector("#what-we-do")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-3 rounded-full border border-jx-ink/15 text-jx-ink font-medium px-7 py-4 transition-[border-color,background-color] duration-300 hover:border-jx-purple/40 hover:bg-jx-purple/5"
            >
              Explore What We Build
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yVisual }} className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.6 }}
            className="relative aspect-square max-w-[600px] mx-auto"
            data-testid="hero-orchestration-visual"
          >
            <svg viewBox="0 0 640 560" className="w-full h-full">
              <defs>
                <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4E2178" />
                  <stop offset="55%" stopColor="#D12D8C" />
                  <stop offset="100%" stopColor="#E86A33" />
                </linearGradient>
              </defs>
              <circle cx={CX} cy={CY} r="200" fill="none" stroke="rgba(78,33,120,0.12)" strokeDasharray="3 8" className="animate-spin-slow" style={{ transformOrigin: "320px 280px" }} />
              <circle cx={CX} cy={CY} r="140" fill="none" stroke="rgba(209,45,140,0.15)" strokeDasharray="2 6" className="animate-spin-slow" style={{ transformOrigin: "320px 280px", animationDirection: "reverse", animationDuration: "28s" }} />
              {NODES.map((n, i) => (
                <g key={n.label}>
                  <motion.line
                    x1={CX} y1={CY} x2={n.x} y2={n.y}
                    stroke="url(#lineGrad)" strokeWidth="1.5" strokeDasharray="5 7"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.5 }}
                    transition={{ duration: 1.2, delay: 1 + i * 0.12, ease: EASE }}
                  />
                  <circle r="3.5" fill="#D12D8C">
                    <animateMotion dur={`${3 + i * 0.4}s`} repeatCount="indefinite" path={`M ${n.x} ${n.y} L ${CX} ${CY}`} />
                  </circle>
                </g>
              ))}
              {NODES.map((n, i) => (
                <motion.g
                  key={`node-${n.label}`}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1.1 + i * 0.12, ease: EASE }}
                  style={{ transformOrigin: `${n.x}px ${n.y}px` }}
                >
                  <circle cx={n.x} cy={n.y} r="34" fill="white" stroke="rgba(78,33,120,0.18)" strokeWidth="1.5" />
                  <circle cx={n.x} cy={n.y} r="34" fill="none" stroke="url(#lineGrad)" strokeWidth="1.5" strokeDasharray="214" strokeDashoffset="160" opacity="0.7" />
                </motion.g>
              ))}
              <motion.g
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                style={{ transformOrigin: "320px 280px" }}
              >
                <circle cx={CX} cy={CY} r="72" fill="white" stroke="url(#lineGrad)" strokeWidth="2.5" />
              </motion.g>
            </svg>

            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 1, ease: EASE }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center pointer-events-none"
            >
              <img src="/assets/logo-mark-sm.png" alt="" width="86" height="43" className="w-[86px] h-auto" loading="lazy" />
              <p className="mt-1.5 text-[9px] font-bold tracking-[0.3em] text-jx-ink/55 uppercase">Intelligent OS</p>
            </motion.div>

            {NODES.map((n, i) => (
              <motion.div
                key={`lbl-${n.label}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3 + i * 0.12 }}
                className="absolute flex flex-col items-center gap-1.5 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${(n.x / 640) * 100}%`, top: `${(n.y / 560) * 100}%` }}
              >
                <n.Icon className="w-5 h-5 text-jx-purple" strokeWidth={1.8} />
                <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-jx-ink/50 whitespace-nowrap bg-jx-paper/80 px-1 rounded">{n.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
